import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { QtClose, QtMenu } from "../components/icons/QtIcon";
import {
  getCandidateDisplayName,
  isCandidateLoggedIn,
  signOutCandidate,
} from "../utils/candidateAuth";
import "./landing.scss";
import "./creatorProgramLanding.scss";
import CareersFooter from "../components/CareersFooter";

const JOURNEY = [
  {
    id: "film",
    step: "01",
    eyebrow: "Record with a friend",
    title: "Shoot the hooks like real UGC",
    body: "Ask a friend to hold the phone. Enter mid-action. Keep it natural. Three visual openings beat one stiff talking head.",
    image: "/creator-journey/creator-journey-01-filming.png",
    alt: "Friend filming a South African creator outdoors with a phone",
  },
  {
    id: "brief",
    step: "02",
    eyebrow: "Read the brief first",
    title: "Every script shows exactly what to do",
    body: "Opening frame, action, expression, spoken hook and payoff. Pick a script, then record Hook A, Hook B, Hook C and the shared body.",
    image: "/creator-journey/creator-journey-02-brief.png",
    alt: "Creator reading a QikTruck script brief on a phone at a cafe",
  },
  {
    id: "upload",
    step: "03",
    eyebrow: "Upload your output",
    title: "Four raw clips. One submission.",
    body: "Upload the vertical videos in the Careers portal, check your previews, then submit for review when all four are ready.",
    image: "/creator-journey/creator-journey-03-upload.png",
    alt: "Creator uploading vertical video clips from phone and laptop",
  },
  {
    id: "paid",
    step: "04",
    eyebrow: "Get paid",
    title: "Approved scripts pay R300",
    body: "When the review passes, payout unlocks. That is the moment the work becomes earnings.",
    image: "/creator-journey/creator-journey-04-paid.png",
    alt: "Creator smiling after receiving payout on her phone",
  },
];

const FAQ = [
  {
    q: "How much do I get paid?",
    a: "R300 per approved script. Payment is for the full set of required clips for that script, not per individual hook.",
  },
  {
    q: "Do I need fancy gear?",
    a: "No. A phone that shoots vertical video is enough. A friend holding the camera often looks more native than a tripod.",
  },
  {
    q: "Can I rewrite the lines?",
    a: "Hooks have an exact spoken line. The shared body lets you speak naturally around guided beats, must-says and banned claims.",
  },
  {
    q: "Who owns the footage?",
    a: "You grant QikTruck a worldwide, perpetual, non-exclusive license to edit, AI-edit uploaded footage, reuse, repost, and commercialise approved Content for profit. Commercial use requires fee approval. Read and accept the Creator Content License before you start.",
  },
  {
    q: "Where do I start?",
    a: "Sign in here, accept the Creator Content License, open Creator Scripts, pick an available script, then start recording from the brief.",
  },
];

export default function CreatorProgramLanding() {
  const [navOpen, setNavOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [displayName, setDisplayName] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoggedIn(isCandidateLoggedIn());
    setDisplayName(getCandidateDisplayName());
  }, []);

  const closeNav = () => setNavOpen(false);
  const handleSignOut = () => {
    closeNav();
    signOutCandidate({ redirectTo: "/" });
  };

  const primaryTo = loggedIn ? "/me/creator" : "/login?next=/me/creator";
  const primaryLabel = loggedIn ? "Open Creator Scripts" : "Start creating";

  return (
    <div className="careers-landing creator-landing">
      <Helmet>
        <title>QikTruck Creator Program - Get paid R300 per approved script</title>
        <meta
          name="description"
          content="Create short-form QikTruck UGC from clear performance briefs. Record 3 visual hooks and 1 shared body, submit in the Careers portal, and get paid R300 per approved script."
        />
        <link rel="canonical" href="https://careers.qiktruck.co.za/creator" />
      </Helmet>

      <div className="careers-landing__bg" aria-hidden="true" />

      <header className="careers-nav">
        <div className="careers-nav__inner">
          <Link to="/" className="careers-nav__brand" onClick={closeNav}>
            QikTruck <span>Careers</span>
          </Link>

          <button
            type="button"
            className="careers-nav__toggle"
            aria-label={navOpen ? "Close menu" : "Open menu"}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            {navOpen ? <QtClose /> : <QtMenu />}
          </button>

          <nav className={`careers-nav__links${navOpen ? " careers-nav__links--open" : ""}`}>
            <a href="#journey" className="careers-nav__link" onClick={closeNav}>
              The journey
            </a>
            <a href="#how-it-works" className="careers-nav__link" onClick={closeNav}>
              How it works
            </a>
            <a href="#faq" className="careers-nav__link" onClick={closeNav}>
              FAQ
            </a>
            <Link to="/" className="careers-nav__link" onClick={closeNav}>
              Careers home
            </Link>
            {loggedIn ? (
              <>
                {displayName ? (
                  <p className="careers-nav__signed-in">
                    Signed in as <strong>{displayName}</strong>
                  </p>
                ) : null}
                <Link to="/me/creator" className="careers-btn careers-btn--primary" onClick={closeNav}>
                  Creator Scripts
                </Link>
                <button
                  type="button"
                  className="careers-btn careers-btn--ghost careers-nav__signout"
                  onClick={handleSignOut}
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link
                to="/login?next=/me/creator"
                className="careers-btn careers-btn--primary"
                onClick={closeNav}
              >
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>

      <section className="creator-landing__hero-visual">
        <div className="creator-landing__hero-media">
          <img
            src="/creator-journey/creator-journey-01-filming.png"
            alt="Friend filming a South African creator outdoors with a phone"
            width={1200}
            height={900}
          />
          <div className="creator-landing__hero-shade" />
          <div className="creator-landing__hero-copy">
            <p className="careers-eyebrow">Creator Program</p>
            <h1 className="careers-h1">
              Film. Upload.
              <br />
              Get paid R300.
            </h1>
            <p className="careers-lead">
              Clear performance briefs. Phone-first vertical clips. A real journey from first take to payout.
            </p>
            <div className="careers-hero__actions">
              <Link to={primaryTo} className="careers-btn careers-btn--primary">
                {primaryLabel}
              </Link>
              <a href="#journey" className="careers-btn careers-btn--secondary">
                See the journey
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="careers-section" id="journey">
        <div className="careers-section-head">
          <p className="careers-eyebrow">Your creator journey</p>
          <h2 className="careers-h2">From first take to payday</h2>
          <p className="careers-lead">
            Scroll the path every creator follows inside the Careers portal.
          </p>
        </div>

        <div className="creator-landing__journey">
          {JOURNEY.map((item, index) => (
            <article
              key={item.id}
              id={item.id}
              className={`creator-landing__beat${
                index % 2 === 1 ? " creator-landing__beat--flip" : ""
              }`}
            >
              <div className="creator-landing__beat-media">
                <img src={item.image} alt={item.alt} loading="lazy" width={900} height={675} />
              </div>
              <div className="creator-landing__beat-copy">
                <span className="creator-landing__step-n">{item.step}</span>
                <p className="careers-eyebrow">{item.eyebrow}</p>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="careers-section" id="how-it-works">
        <div className="careers-section-head">
          <p className="careers-eyebrow">Inside the portal</p>
          <h2 className="careers-h2">What you actually do</h2>
        </div>
        <div className="creator-landing__steps">
          <article className="creator-landing__step">
            <span className="creator-landing__step-n">1</span>
            <h3>Sign in on Careers</h3>
            <p>Open Creator Scripts with your email. No agency middleman.</p>
          </article>
          <article className="creator-landing__step">
            <span className="creator-landing__step-n">2</span>
            <h3>Pick a script</h3>
            <p>Each available script pays R300 when the full set is approved.</p>
          </article>
          <article className="creator-landing__step">
            <span className="creator-landing__step-n">3</span>
            <h3>Record 4 raw clips</h3>
            <p>Hook A, Hook B, Hook C and one shared body in vertical 9:16.</p>
          </article>
          <article className="creator-landing__step">
            <span className="creator-landing__step-n">4</span>
            <h3>Submit and get paid</h3>
            <p>Upload, submit for review, unlock payout when approved.</p>
          </article>
        </div>
      </section>

      <section className="careers-section" id="faq">
        <div className="careers-section-head">
          <p className="careers-eyebrow">FAQ</p>
          <h2 className="careers-h2">Before you start</h2>
        </div>
        <div className="creator-landing__faq">
          {FAQ.map((item) => (
            <details key={item.q} className="creator-landing__faq-item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="careers-section creator-landing__cta">
        <div className="creator-landing__cta-panel creator-landing__cta-panel--photo">
          <img
            src="/creator-journey/creator-journey-04-paid.png"
            alt=""
            aria-hidden="true"
            className="creator-landing__cta-bg"
          />
          <div className="creator-landing__cta-content">
            <h2 className="careers-h2">Ready for your first payout?</h2>
            <p className="careers-lead">
              Open Creator Scripts, pick a brief, and start with Hook A.
            </p>
            <Link to={primaryTo} className="careers-btn careers-btn--primary">
              {primaryLabel}
            </Link>
          </div>
        </div>
      </section>

      <CareersFooter />
    </div>
  );
}
